interface OrganizationSchema {
  type: 'Organization'
  name: string
  url: string
  logo: string
  description: string
  sameAs?: string[]
}

interface WebSiteSchema {
  type: 'WebSite'
  name: string
  url: string
  description: string
}

interface PodcastEpisodeSchema {
  type: 'PodcastEpisode'
  name: string
  description: string
  url: string
  datePublished: string
  duration: string
  image?: string
  // Optional: an archive can host more than one series, so the series name
  // cannot be derived from the episode alone. Omitted rather than guessed when
  // the series is not available.
  partOfSeries?: {
    name: string
    url: string
  }
}

interface BreadcrumbSchema {
  type: 'BreadcrumbList'
  items: Array<{
    name: string
    url: string
  }>
}

type SchemaType = OrganizationSchema | WebSiteSchema | PodcastEpisodeSchema | BreadcrumbSchema

function formatDurationISO(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `PT${hours > 0 ? hours + 'H' : ''}${minutes}M${secs}S`
}

function buildSchema(schema: SchemaType): object {
  const BASE_URL = 'https://thearchivenetwork.com'

  switch (schema.type) {
    case 'Organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: schema.name,
        url: schema.url,
        logo: schema.logo,
        description: schema.description,
        sameAs: schema.sameAs || [],
      }

    case 'WebSite':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: schema.name,
        url: schema.url,
        description: schema.description,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${BASE_URL}/explore?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      }

    case 'PodcastEpisode':
      return {
        '@context': 'https://schema.org',
        '@type': 'PodcastEpisode',
        name: schema.name,
        description: schema.description,
        url: schema.url,
        datePublished: schema.datePublished,
        timeRequired: schema.duration,
        image: schema.image,
        ...(schema.partOfSeries && {
          partOfSeries: {
            '@type': 'PodcastSeries',
            name: schema.partOfSeries.name,
            url: schema.partOfSeries.url,
          },
        }),
      }

    case 'BreadcrumbList':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: schema.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      }

    default:
      return {}
  }
}

interface JsonLdProps {
  schema: SchemaType | SchemaType[]
}

export default function JsonLd({ schema }: JsonLdProps) {
  const schemas = Array.isArray(schema) ? schema : [schema]
  const jsonLd = schemas.map(buildSchema)

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(jsonLd.length === 1 ? jsonLd[0] : jsonLd),
      }}
    />
  )
}

// Helper to create podcast episode schema.
//
// `series` is the episode's own series, resolved by series_id. It is required to
// name the series correctly: an archive can host several series (e.g. the
// "Dialogues" companion to each main show), so `The <Archive_key> Archive
// Podcast` names the wrong show for every episode that is not in the archive's
// original series. When the series is unavailable, partOfSeries is left off
// rather than guessed — omitting a property is valid schema.org; asserting a
// false one is not.
export function createPodcastEpisodeSchema(
  episode: {
    id: string
    title: string
    summary: string
    published_at: string
    duration_sec: number
    cover_image_url?: string
    archive_key: string
  },
  series?: { title: string; cover_image_url?: string } | null
): PodcastEpisodeSchema {
  const BASE_URL = 'https://thearchivenetwork.com'
  const absolute = (url: string) => (url.startsWith('/') ? `${BASE_URL}${url}` : url)

  return {
    type: 'PodcastEpisode',
    name: episode.title,
    description: episode.summary || '',
    url: `${BASE_URL}/podcast/${episode.id}`,
    datePublished: episode.published_at,
    duration: formatDurationISO(episode.duration_sec),
    image:
      episode.cover_image_url ||
      (series?.cover_image_url && absolute(series.cover_image_url)) ||
      `${BASE_URL}/images/podcasts/${episode.archive_key}.jpg`,
    ...(series && {
      partOfSeries: {
        name: series.title,
        url: `${BASE_URL}/podcasts`,
      },
    }),
  }
}

// Organization schema for homepage
export const organizationSchema: OrganizationSchema = {
  type: 'Organization',
  name: 'The Archive Network',
  url: 'https://thearchivenetwork.com',
  logo: 'https://thearchivenetwork.com/images/archive-network-seal.png',
  description: 'The unified gateway to humanity\'s memory. A growing network of interconnected archives documenting treaties, conflicts, civilizations, company origins, lineages, classified secrets, explorations, and mythology.',
  sameAs: [
    'https://www.patreon.com/thearchivenetwork',
  ],
}

// Website schema for homepage
export const websiteSchema: WebSiteSchema = {
  type: 'WebSite',
  name: 'The Archive Network',
  url: 'https://thearchivenetwork.com',
  description: 'The unified gateway to humanity\'s memory.',
}
